/**
 * urql GraphQL Client with subscriptions support
 *
 * Sets up:
 * - HTTP requests for queries/mutations
 * - WebSocket for subscriptions
 * - Normalized cache (graphcache)
 * - Auth token from localStorage
 * - Auth error handling (auto-redirect to login)
 */

import { createClient, fetchExchange, subscriptionExchange, mapExchange } from 'urql';
import { cacheExchange } from '@urql/exchange-graphcache';
import { createClient as createWSClient } from 'graphql-ws';
import createDebug from 'debug';
import { getAuthToken, clearAuthToken } from '../api/client';

const debug = createDebug('app:graphql');

/**
 * Check if an error is an authentication error
 */
function isAuthError(error: { message?: string; graphQLErrors?: Array<{ message: string }> }): boolean {
	const authMessages = ['Authentication required', 'Unauthorized', 'Not authenticated'];

	// Check main error message
	if (error.message && authMessages.some((msg) => error.message!.includes(msg))) {
		return true;
	}

	// Check GraphQL errors
	if (error.graphQLErrors) {
		return error.graphQLErrors.some((gqlErr) =>
			authMessages.some((msg) => gqlErr.message.includes(msg))
		);
	}

	return false;
}

/**
 * Handle auth errors by clearing token and redirecting to login
 */
function handleAuthError() {
	debug('auth error detected, clearing token and redirecting to login');
	clearAuthToken();

	// Only redirect if not already on login page
	if (!window.location.pathname.startsWith('/login')) {
		window.location.href = '/login';
	}
}

// Determine GraphQL endpoint based on environment
const GRAPHQL_HTTP_URL = '/graphql';
const GRAPHQL_WS_URL = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/graphql`;

// Create WebSocket client for subscriptions
const wsClient = createWSClient({
	url: GRAPHQL_WS_URL,
	connectionParams: () => {
		const token = getAuthToken();
		debug('ws:connect hasToken=%s url=%s', !!token, GRAPHQL_WS_URL);
		return token ? { authorization: `Bearer ${token}` } : {};
	},
	on: {
		connected: (socket) => debug('ws:connected protocol=%s', (socket as WebSocket).protocol),
		closed: (event) => debug('ws:closed code=%d reason=%s', (event as CloseEvent).code, (event as CloseEvent).reason || 'none'),
		error: (err) => debug('ws:error %o', err),
		message: (msg: unknown) => debug('ws:message %s', typeof msg === 'string' ? msg.slice(0, 100) : JSON.stringify(msg).slice(0, 100)),
	},
	retryAttempts: 5,
	shouldRetry: () => true,
});

// Configure normalized cache with entity keys
const cache = cacheExchange({
	keys: {
		// Define how entities are keyed in the cache
		Link: (data) => (data.id as string) || null,
		Queue: (data) => (data.id as string) || null,
		Tag: (data) => (data.id as string) || null,
		Category: (data) => (data.id as string) || null,
		Download: (data) => (data.id as string) || null,
		DownloadFile: () => null,
		DownloadStats: () => null,
		LinkConnection: () => null, // Don't normalize connections
		QueueConnection: () => null,
		TagConnection: () => null,
		PageInfo: () => null,
		UserError: () => null,
	},
	updates: {
		Mutation: {
			// When a link is created, invalidate the links query
			createLink: (_result, _args, cache) => {
				debug('cache update: createLink');
				cache.invalidate('Query', 'links');
			},
			// When a link is archived/deleted, invalidate queries
			archiveLink: (_result, _args, cache) => {
				debug('cache update: archiveLink');
				cache.invalidate('Query', 'links');
			},
			unarchiveLink: (_result, _args, cache) => {
				debug('cache update: unarchiveLink');
				cache.invalidate('Query', 'links');
			},
			deleteLink: (_result, _args, cache) => {
				debug('cache update: deleteLink');
				cache.invalidate('Query', 'links');
			},
			// When a link is moved to another queue
			assignLinkToQueue: (_result, _args, cache) => {
				debug('cache update: assignLinkToQueue');
				cache.invalidate('Query', 'links');
				cache.invalidate('Query', 'queues');
			},
			// Queue mutations
			createQueue: (_result, _args, cache) => {
				debug('cache update: createQueue');
				cache.invalidate('Query', 'queues');
			},
			deleteQueue: (_result, _args, cache) => {
				debug('cache update: deleteQueue');
				cache.invalidate('Query', 'queues');
			},
			reorderQueues: (_result, _args, cache) => {
				debug('cache update: reorderQueues');
				cache.invalidate('Query', 'queues');
			},
			// Download mutations
			triggerDownload: (_result, _args, cache) => {
				debug('cache update: triggerDownload');
				cache.invalidate('Query', 'download');
				cache.invalidate('Query', 'downloadStats');
			},
			cancelDownload: (_result, _args, cache) => {
				debug('cache update: cancelDownload');
				cache.invalidate('Query', 'download');
				cache.invalidate('Query', 'downloadStats');
			},
			retryDownload: (_result, _args, cache) => {
				debug('cache update: retryDownload');
				cache.invalidate('Query', 'download');
				cache.invalidate('Query', 'downloadStats');
			},
		},
		Subscription: {
			// Handle subscription events by invalidating relevant queries
			linkChanges: (_result, _args, cache) => {
				debug('subscription update: linkChanges');
				cache.invalidate('Query', 'links');
			},
			queueLinkChanges: (_result, _args, cache) => {
				debug('subscription update: queueLinkChanges');
				cache.invalidate('Query', 'links');
			},
			queueChanges: (_result, _args, cache) => {
				debug('subscription update: queueChanges');
				cache.invalidate('Query', 'queues');
			},
			downloadChanges: (_result, _args, cache) => {
				debug('subscription update: downloadChanges');
				cache.invalidate('Query', 'download');
				cache.invalidate('Query', 'downloadStats');
			},
		},
	},
});

// Auth error exchange - intercepts errors and redirects to login on auth failures
const authErrorExchange = mapExchange({
	onError(error) {
		debug('graphql error: %s', error.message);

		if (isAuthError(error)) {
			handleAuthError();
		}
	},
});

// Create the urql client
export const urqlClient = createClient({
	url: GRAPHQL_HTTP_URL,
	exchanges: [
		cache,
		authErrorExchange, // Must be before fetchExchange to intercept errors
		fetchExchange,
		subscriptionExchange({
			forwardSubscription(request) {
				const input = { ...request, query: request.query || '' };
				return {
					subscribe(sink) {
						const unsubscribe = wsClient.subscribe(input, sink);
						return { unsubscribe };
					},
				};
			},
		}),
	],
	fetchOptions: () => {
		const token = getAuthToken();
		return token
			? { headers: { Authorization: `Bearer ${token}` } }
			: {};
	},
});

// Export the WS client for manual control if needed
export { wsClient };

// Re-export types for convenience
export type { Client } from 'urql';
